import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X, Plus, Pencil, Trash2 } from 'lucide-react';
import {
  getConversations,
  searchConversations,
  updateConversationTitle,
  deleteConversation,
} from '../../services/conversations';
import drawerStyles from './chatHistoryDrawer.module.css';

const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ChatHistoryDrawer({
  isOpen,
  onClose,
  onNewChat,
  onLoadConversation,
  currentConversationId,
  refreshKey,
}) {
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const closeButtonRef = useRef(null);

  const fetchConversations = async (page = 1, search = '') => {
    setIsLoading(true);
    setError(null);
    try {
      const result = search
        ? await searchConversations(search, page, 20)
        : await getConversations(page, 20);
      const { conversations: convs, pagination: pag } = result.data;
      if (page === 1) {
        setConversations(convs);
      } else {
        setConversations((prev) => [...prev, ...convs]);
      }
      setPagination(pag);
    } catch (err) {
      setError('Failed to load conversations.');
      console.error('Error fetching conversations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch on open and when refreshKey changes
  useEffect(() => {
    if (isOpen) {
      fetchConversations(1, searchQuery);
    }
  }, [isOpen, refreshKey]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    const timerId = setTimeout(() => {
      fetchConversations(1, searchQuery);
    }, 300);
    return () => clearTimeout(timerId);
  }, [searchQuery]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus close button on open
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setSearchQuery('');
      setRenamingId(null);
      onClose();
    }, 200);
  };

  const handleRenameSubmit = async (id) => {
    const trimmed = renameValue.trim();
    const original = conversations.find((c) => c.id === id);
    if (!trimmed || trimmed === original?.title) {
      setRenamingId(null);
      return;
    }
    try {
      await updateConversationTitle(id, trimmed);
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title: trimmed } : c)));
    } catch (err) {
      console.error('Failed to rename conversation:', err);
    }
    setRenamingId(null);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      <div
        className={`${drawerStyles.backdrop} ${isClosing ? drawerStyles.backdropClosing : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        className={`${drawerStyles.drawerPanel} ${isClosing ? drawerStyles.drawerPanelClosing : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Chat history"
      >
        <div className={drawerStyles.drawerHeader}>
          <h2 className={drawerStyles.drawerTitle}>Chat History</h2>
          <button
            ref={closeButtonRef}
            className={drawerStyles.closeButton}
            onClick={handleClose}
            aria-label="Close chat history"
          >
            <X size={20} />
          </button>
        </div>

        <button
          className={drawerStyles.newChatButton}
          onClick={() => {
            onNewChat();
            handleClose();
          }}
        >
          <Plus size={16} /> New Chat
        </button>

        <div className={drawerStyles.searchContainer}>
          <input
            type="text"
            className={drawerStyles.searchInput}
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search conversations"
          />
        </div>

        <ul className={drawerStyles.conversationList}>
          {error && <li className={drawerStyles.errorState}>{error}</li>}

          {!error && !isLoading && conversations.length === 0 && (
            <li className={drawerStyles.emptyState}>
              {searchQuery ? 'No conversations match your search.' : 'No conversations yet.'}
            </li>
          )}

          {conversations.map((conv) => (
            <li
              key={conv.id}
              className={`${drawerStyles.conversationItem} ${
                conv.id === currentConversationId ? drawerStyles.conversationItemActive : ''
              }`}
              onClick={() => {
                if (renamingId !== conv.id) {
                  onLoadConversation(conv.id);
                  handleClose();
                }
              }}
            >
              <div className={drawerStyles.conversationContent}>
                {renamingId === conv.id ? (
                  <input
                    className={drawerStyles.renameInput}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameSubmit(conv.id);
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    onBlur={() => handleRenameSubmit(conv.id)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    maxLength={256}
                    aria-label="Rename conversation"
                  />
                ) : (
                  <>
                    <p className={drawerStyles.conversationTitle}>{conv.title}</p>
                    <p className={drawerStyles.conversationMeta}>
                      {conv.messageCount} messages &middot; {formatTimestamp(conv.updatedAt)}
                    </p>
                  </>
                )}
              </div>

              <div className={drawerStyles.conversationActions}>
                <button
                  className={drawerStyles.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRenamingId(conv.id);
                    setRenameValue(conv.title);
                  }}
                  title="Rename"
                  aria-label={`Rename "${conv.title}"`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className={`${drawerStyles.actionButton} ${drawerStyles.deleteButton}`}
                  onClick={(e) => handleDelete(e, conv.id)}
                  title="Delete"
                  aria-label={`Delete "${conv.title}"`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}

          {isLoading && <li className={drawerStyles.emptyState}>Loading...</li>}
        </ul>

        {pagination.page < pagination.totalPages && !isLoading && (
          <button
            className={drawerStyles.loadMoreButton}
            onClick={() => fetchConversations(pagination.page + 1, searchQuery)}
          >
            Load more conversations
          </button>
        )}
      </div>
    </>
  );
}

ChatHistoryDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  onLoadConversation: PropTypes.func.isRequired,
  currentConversationId: PropTypes.string,
  refreshKey: PropTypes.number.isRequired,
};
